import { useState, type FormEvent } from "react";

import { friendlyAuthError } from "../../auth/authErrors";
import { EyeIcon, EyeOffIcon } from "./icons";
import { forgotPasswordUrl, loginHelpUrl } from "./loginLinks";

/**
 * Email/password (ROPC) sign-in form, shared by both places a B2C provider
 * can be signed into:
 *
 * - `variant="primary"` — the Atlas account form that IS the login card.
 *   Matches Atlas Desktop: sentence-case field labels, a "Forgot password?"
 *   link on the Password row, a "Login" CTA, and "Need login assistance?"
 *   underneath. The two links render only when their env vars are set
 *   (see loginLinks.ts).
 * - `variant="secondary"` — the same form inside the advisory modal for a
 *   non-Atlas provider. No support links (they are Atlas-specific), a
 *   "Sign in" CTA, and no heading of its own — the caller supplies one, so
 *   the form never has to know which provider it is collecting for.
 */
export interface B2cFormProps {
  variant: "primary" | "secondary";
  onSubmit: (email: string, password: string) => Promise<void>;
  /** Rendered under the CTA when present (the modal's own back affordance). */
  footer?: React.ReactNode;
}

export default function B2cForm({ variant, onSubmit, footer }: B2cFormProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [pending, setPending] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const isPrimary = variant === "primary";
  // Ids must stay unique across the two variants — the primary form and the
  // modal's form can be in the DOM at the same time, and duplicate ids break
  // <label htmlFor> as well as password-manager field matching.
  const emailId = `login-email-${variant}`;
  const passwordId = `login-password-${variant}`;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password || pending) return;
    setPending(true);
    setLocalError(null);
    try {
      await onSubmit(email.trim(), password);
      // Our login is a fetch() + in-app nav — there's no traditional
      // page-navigation event for Chrome's password manager to hook onto, so
      // autosave prompts don't fire on their own. The Credential Management
      // API lets us hand the browser the credential explicitly after a
      // successful auth. Fire-and-forget: never block the redirect on it.
      void maybeSaveCredential(email.trim(), password);
    } catch (err) {
      setLocalError(
        friendlyAuthError(
          err,
          "Sign in failed. Check your credentials and try again.",
        ),
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="w-full flex flex-col gap-4">
      {/*
        id + name on the inputs are load-bearing for third-party password
        managers: 1Password / Bitwarden / LastPass pattern-match on them.
        autoComplete drives Chrome's built-in.
      */}
      <label htmlFor={emailId} className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-foreground">Email</span>
        <input
          id={emailId}
          name="username"
          type="email"
          autoComplete="username"
          autoFocus
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="rounded-xl border border-input bg-background px-4 py-3 text-base text-foreground placeholder:text-muted-foreground hover:border-ring focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary transition-all disabled:opacity-60"
          disabled={pending}
        />
      </label>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between gap-3">
          <label
            htmlFor={passwordId}
            className="text-sm font-medium text-foreground"
          >
            Password
          </label>
          {isPrimary && forgotPasswordUrl && (
            <a
              href={forgotPasswordUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-primary dark:text-[hsl(324,60%,70%)] hover:underline"
            >
              Forgot password?
            </a>
          )}
        </div>
        <div className="relative">
          <input
            id={passwordId}
            name="password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-xl border border-input bg-background pl-4 pr-12 py-3 text-base text-foreground placeholder:text-muted-foreground hover:border-ring focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary transition-all disabled:opacity-60"
            disabled={pending}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Hide password" : "Show password"}
            aria-pressed={showPassword}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md p-1 transition-colors"
            tabIndex={pending ? -1 : 0}
          >
            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        </div>
      </div>

      {localError && (
        <p role="alert" className="text-sm text-destructive font-medium m-0">
          {localError}
        </p>
      )}

      {/*
        Gradient CTA mirrors Provider Portal's login button — `brightness-110`
        plus a plum-tinted drop-shadow lifts it on hover without needing a
        darker solid fill underneath.
      */}
      <button
        type="submit"
        disabled={pending}
        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white bg-gradient-to-br from-[#8a1558] to-[#c42a83] hover:brightness-110 hover:shadow-[0_4px_14px_rgba(138,21,88,0.3)] text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        {pending ? "Signing in…" : isPrimary ? "Login" : "Sign in"}
      </button>

      {isPrimary && loginHelpUrl && (
        <a
          href={loginHelpUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-medium text-primary dark:text-[hsl(324,60%,70%)] hover:underline text-center"
        >
          Need login assistance?
        </a>
      )}

      {footer}
    </form>
  );
}

/**
 * Hand a successful credential to the browser's built-in password manager via
 * the Credential Management API. Chrome wires this up to its autosave prompt;
 * Safari ignores the call gracefully; third-party password managers don't
 * listen to this API and rely on their own heuristics (hence the
 * `id`/`name`/`autoComplete` attributes on the inputs above).
 *
 * All failures are silent — we're purely trying to be friendlier to a working
 * API, never blocking auth on it.
 */
async function maybeSaveCredential(
  id: string,
  password: string,
): Promise<void> {
  try {
    const w = window as Window & {
      PasswordCredential?: new (init: {
        id: string;
        password: string;
        name?: string;
      }) => Credential;
    };
    if (!w.PasswordCredential || !navigator.credentials?.store) return;
    const cred = new w.PasswordCredential({ id, password, name: id });
    await navigator.credentials.store(cred);
  } catch {
    // best-effort
  }
}
